import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, updateDoc } from 'firebase/firestore';
import { sendPasswordResetEmail, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { db, auth } from '../firebaseConfig';
import { useUser } from '../context/UserContext';
import Swal from 'sweetalert2';

interface UserCredential {
  id: string;
  nombre: string;
  correo: string;
  tipo: 'asesor' | 'asociado';
  firebase_uid?: string;
  agencia?: string;
}

interface ResetResult {
  email: string;
  success: boolean;
  error?: string;
}

export default function PasswordResetManager() {
  const { user } = useUser();
  const [usuarios, setUsuarios] = useState<UserCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [resetResults, setResetResults] = useState<ResetResult[]>([]);
  const [filtro, setFiltro] = useState('');
  const [filtroEstado, setFiltroEstado] = useState<'todos' | 'con_cuenta' | 'sin_cuenta' | 'sin_email'>('todos');
  const [filtroTipo, setFiltroTipo] = useState<'todos' | 'asesor' | 'asociado'>('todos');
  const [creating, setCreating] = useState(false);
  const [createdAccounts, setCreatedAccounts] = useState<{email: string, password: string, uid: string}[]>([]);

  useEffect(() => {
    const fetchUsuarios = async () => {
      try {
        const usuariosData: UserCredential[] = [];

        // Cargar TODOS los asesores (incluso sin email)
        const asesoresSnap = await getDocs(collection(db, 'asesor'));
        for (const doc of asesoresSnap.docs) {
          const data = doc.data();
          // Incluir TODOS los registros para análisis completo
          
          // Buscar agencia
          let agenciaNombre = 'Sin agencia';
          if (data.inmobiliaria_id) {
            try {
              const agenciaId = Array.isArray(data.inmobiliaria_id) ? data.inmobiliaria_id[0] : data.inmobiliaria_id;
              const agenciaQuery = query(
                collection(db, 'inmobiliaria'), 
                where('inmobiliaria_id', '==', agenciaId)
              );
              const agenciaSnap = await getDocs(agenciaQuery);
              if (!agenciaSnap.empty) {
                agenciaNombre = agenciaSnap.docs[0].data().nombre;
              }
            } catch (e) {
              console.log('Error buscando agencia:', e);
            }
          }

          usuariosData.push({
            id: doc.id,
            nombre: data.nombre || 'Sin nombre',
            correo: data.correo || 'Sin email',
            tipo: 'asesor',
            firebase_uid: data.firebase_uid,
            agencia: agenciaNombre
          });
        }

        // Cargar TODOS los asociados (incluso sin email)
        const asociadosSnap = await getDocs(collection(db, 'asociado'));
        for (const doc of asociadosSnap.docs) {
          const data = doc.data();
          // Incluir TODOS los registros para análisis completo
          
          // Buscar agencia
          let agenciaNombre = 'Sin agencia';
          if (data.inmobiliaria_id) {
            try {
              const agenciaQuery = query(
                collection(db, 'inmobiliaria'), 
                where('inmobiliaria_id', '==', data.inmobiliaria_id)
              );
              const agenciaSnap = await getDocs(agenciaQuery);
              if (!agenciaSnap.empty) {
                agenciaNombre = agenciaSnap.docs[0].data().nombre;
              }
            } catch (e) {
              console.log('Error buscando agencia:', e);
            }
          }

          usuariosData.push({
            id: doc.id,
            nombre: data.nombre || 'Sin nombre',
            correo: data.correo || 'Sin email',
            tipo: 'asociado',
            firebase_uid: data.firebase_uid,
            agencia: agenciaNombre
          });
        }

        usuariosData.sort((a, b) => a.nombre.localeCompare(b.nombre));
        setUsuarios(usuariosData);

      } catch (error) {
        console.error('Error cargando usuarios:', error);
        Swal.fire('Error', 'No se pudieron cargar los usuarios.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchUsuarios();
  }, []);

  // Solo admins pueden usar este componente
  if (!user || !user.tipo?.includes('admin')) {
    return (
      <div className="alert alert-danger">
        <h6>Acceso Denegado</h6>
        <p>Solo administradores pueden gestionar credenciales de usuarios.</p>
      </div>
    );
  }

  const sendPasswordReset = async (usuario: UserCredential) => {
    try {
      await sendPasswordResetEmail(auth, usuario.correo);
      return { email: usuario.correo, success: true };
    } catch (error: any) {
      return { 
        email: usuario.correo, 
        success: false, 
        error: error.message 
      };
    }
  };

  const handleSingleReset = async (usuario: UserCredential) => {
    const confirm = await Swal.fire({
      title: 'Enviar Reset de Contraseña',
      text: `Se enviará un email de recuperación a ${usuario.correo}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, enviar',
      cancelButtonText: 'Cancelar'
    });

    if (!confirm.isConfirmed) return;

    setResetting(true);
    const result = await sendPasswordReset(usuario);
    
    if (result.success) {
      Swal.fire('Enviado', `Email de reset enviado a ${usuario.correo}`, 'success');
    } else {
      Swal.fire('Error', `Error enviando email: ${result.error}`, 'error');
    }
    setResetting(false);
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const handleCreateAccount = async (usuario: UserCredential) => {
    if (!usuario.correo.includes('@')) {
      Swal.fire('Error', 'El email no es válido', 'error');
      return;
    }

    const confirm = await Swal.fire({
      title: 'Crear Cuenta Firebase',
      text: `Se creará una cuenta para ${usuario.nombre} (${usuario.correo})`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, crear',
      cancelButtonText: 'Cancelar'
    });

    if (!confirm.isConfirmed) return;

    setCreating(true);
    const password = generateRandomPassword();

    try {
      // Crear cuenta en Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, usuario.correo, password);
      const firebaseUser = userCredential.user;

      // Actualizar perfil con el nombre
      await updateProfile(firebaseUser, {
        displayName: usuario.nombre
      });

      // Actualizar documento en Firestore con firebase_uid
      const collectionName = usuario.tipo === 'asesor' ? 'asesor' : 'asociado';
      await updateDoc(doc(db, collectionName, usuario.id), {
        firebase_uid: firebaseUser.uid
      });

      // Actualizar estado local
      setUsuarios(prevUsuarios =>
        prevUsuarios.map(u =>
          u.id === usuario.id ? {...u, firebase_uid: firebaseUser.uid} : u
        )
      );

      // Guardar credenciales creadas
      const newAccount = {
        email: usuario.correo,
        password: password,
        uid: firebaseUser.uid
      };
      setCreatedAccounts(prev => [...prev, newAccount]);

      // Sprint 2 (F5A-003): enmascarar password con toggle show/hide.
      // Evita exposición accidental via screenshot / hombro.
      const masked = password.slice(0, 2) + '•'.repeat(Math.max(0, password.length - 4)) + password.slice(-2);
      Swal.fire({
        title: 'Cuenta Creada',
        html: `
          <div class="text-start">
            <p><strong>✅ Cuenta creada exitosamente</strong></p>
            <p><strong>Email:</strong> ${usuario.correo}</p>
            <p>
              <strong>Contraseña temporal:</strong>
              <code id="swal-pwd-display" data-pwd="${password}">${masked}</code>
              <button type="button" id="swal-pwd-toggle" class="btn btn-sm btn-outline-secondary ms-2"
                      aria-label="Mostrar/ocultar contraseña" style="padding:2px 8px;">👁</button>
              <button type="button" id="swal-pwd-copy" class="btn btn-sm btn-outline-primary ms-1"
                      aria-label="Copiar contraseña" style="padding:2px 8px;">Copiar</button>
            </p>
            <p class="text-warning mt-3">
              <i class="fas fa-exclamation-triangle me-2"></i>
              Envía esta contraseña al usuario por canal seguro. El usuario debe cambiarla en el primer login.
            </p>
          </div>
        `,
        icon: 'success',
        didOpen: () => {
          const display = document.getElementById('swal-pwd-display') as HTMLElement | null;
          const toggle = document.getElementById('swal-pwd-toggle') as HTMLButtonElement | null;
          const copy = document.getElementById('swal-pwd-copy') as HTMLButtonElement | null;
          if (!display || !toggle || !copy) return;
          let shown = false;
          toggle.addEventListener('click', () => {
            shown = !shown;
            display.textContent = shown ? password : masked;
          });
          copy.addEventListener('click', () => {
            navigator.clipboard?.writeText(password).then(() => {
              copy.textContent = '✓ Copiada';
              setTimeout(() => { copy.textContent = 'Copiar'; }, 1500);
            });
          });
        },
      });
    } catch (error: any) {
      console.error('Error creando cuenta:', error);
      Swal.fire({
        title: 'Error',
        text: `No se pudo crear la cuenta: ${error.message}`,
        icon: 'error'
      });
    } finally {
      setCreating(false);
    }
  };

  const handleBulkCreate = async () => {
    const usuariosSinCuenta = usuariosFiltrados.filter(u =>
      !u.firebase_uid &&
      u.correo !== 'Sin email' &&
      u.correo.includes('@')
    );

    if (usuariosSinCuenta.length === 0) {
      Swal.fire('Info', 'No hay usuarios sin cuenta para crear', 'info');
      return;
    }

    const confirm = await Swal.fire({
      title: 'Creación Masiva de Cuentas',
      text: `Se crearán ${usuariosSinCuenta.length} cuentas. Esto puede tomar varios minutos. ¿Continuar?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, crear todas',
      cancelButtonText: 'Cancelar'
    });

    if (!confirm.isConfirmed) return;

    setCreating(true);
    const results: {email: string, password: string, uid: string}[] = [];
    let exitosos = 0;
    let fallidos = 0;

    // Procesar en lotes de 5 para evitar límites de Firebase
    const batchSize = 5;
    const batches = [];
    for (let i = 0; i < usuariosSinCuenta.length; i += batchSize) {
      batches.push(usuariosSinCuenta.slice(i, i + batchSize));
    }

    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];
      console.log(`Procesando lote ${batchIndex + 1}/${batches.length}`);

      for (const usuario of batch) {
        const password = generateRandomPassword();

        try {
          const userCredential = await createUserWithEmailAndPassword(auth, usuario.correo, password);
          const firebaseUser = userCredential.user;

          await updateProfile(firebaseUser, {
            displayName: usuario.nombre
          });

          const collectionName = usuario.tipo === 'asesor' ? 'asesor' : 'asociado';
          await updateDoc(doc(db, collectionName, usuario.id), {
            firebase_uid: firebaseUser.uid
          });

          results.push({
            email: usuario.correo,
            password: password,
            uid: firebaseUser.uid
          });
          exitosos++;
        } catch (error: any) {
          console.error('Error creando cuenta:', error);
          fallidos++;
        }
      }

      // Esperar 3 segundos entre lotes para evitar límites
      if (batchIndex < batches.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 3000));
      }
    }

    setCreatedAccounts(results);
    setCreating(false);

    // Recargar usuarios para actualizar la vista
    window.location.reload();

    Swal.fire({
      title: 'Creación Completada',
      html: `
        <div class="text-start">
          <p><strong>✅ Exitosos:</strong> ${exitosos}</p>
          <p><strong>❌ Fallidos:</strong> ${fallidos}</p>
          <p><strong>📧 Total procesados:</strong> ${exitosos + fallidos}</p>
          ${exitosos > 0 ? '<p class="text-warning mt-3"><i class="fas fa-download me-2"></i>Las credenciales se han guardado</p>' : ''}
        </div>
      `,
      icon: fallidos > 0 ? 'warning' : 'success'
    });
  };

  const handleBulkReset = async () => {
    const usuariosParaReset = usuariosFiltrados.filter(u => u.firebase_uid && u.correo !== 'Sin email');

    const confirm = await Swal.fire({
      title: 'Reset Masivo de Contraseñas',
      text: `Se enviará email de reset a ${usuariosParaReset.length} usuarios. ¿Continuar?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, enviar a todos',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33'
    });

    if (!confirm.isConfirmed) return;

    setResetting(true);
    setResetResults([]);

    const results: ResetResult[] = [];
    let processed = 0;

    for (const usuario of usuariosParaReset) {
      const result = await sendPasswordReset(usuario);
      results.push(result);
      processed++;
      
      // Mostrar progreso cada 10 usuarios
      if (processed % 10 === 0) {
        console.log(`Procesados ${processed}/${usuariosParaReset.length} usuarios`);
      }
    }

    setResetResults(results);
    setResetting(false);

    const exitosos = results.filter(r => r.success).length;
    const fallidos = results.filter(r => !r.success).length;

    Swal.fire({
      title: 'Reset Completado',
      html: `
        <div class="text-start">
          <p><strong>✅ Exitosos:</strong> ${exitosos}</p>
          <p><strong>❌ Fallidos:</strong> ${fallidos}</p>
          <p><strong>📧 Total procesados:</strong> ${results.length}</p>
        </div>
      `,
      icon: fallidos > 0 ? 'warning' : 'success'
    });
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Cargando usuarios...</span>
        </div>
        <p className="mt-2">Cargando usuarios con cuentas Firebase...</p>
      </div>
    );
  }

  // Aplicar filtros
  let usuariosFiltrados = usuarios;

  // Filtro por texto
  if (filtro.trim() !== '') {
    usuariosFiltrados = usuariosFiltrados.filter(u => 
      u.nombre.toLowerCase().includes(filtro.toLowerCase()) ||
      u.correo.toLowerCase().includes(filtro.toLowerCase()) ||
      u.agencia?.toLowerCase().includes(filtro.toLowerCase())
    );
  }

  // Filtro por estado de cuenta Firebase
  if (filtroEstado !== 'todos') {
    usuariosFiltrados = usuariosFiltrados.filter(u => {
      switch (filtroEstado) {
        case 'con_cuenta':
          return u.firebase_uid && u.correo !== 'Sin email';
        case 'sin_cuenta':
          return !u.firebase_uid && u.correo !== 'Sin email';
        case 'sin_email':
          return u.correo === 'Sin email' || !u.correo || !u.correo.includes('@');
        default:
          return true;
      }
    });
  }

  // Filtro por tipo
  if (filtroTipo !== 'todos') {
    usuariosFiltrados = usuariosFiltrados.filter(u => u.tipo === filtroTipo);
  }

  return (
    <div className="password-reset-manager">
      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">
            <i className="fas fa-key me-2"></i>
            Gestión de Credenciales - Reset de Contraseñas
          </h5>
        </div>
        <div className="card-body">
          {/* Estadísticas */}
          <div className="row mb-4">
            <div className="col-md-2">
              <div className="card text-center bg-info text-white">
                <div className="card-body">
                  <h3>{usuarios.length}</h3>
                  <p className="mb-0">Con Email</p>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card text-center bg-success text-white">
                <div className="card-body">
                  <h3>{usuarios.filter(u => u.tipo === 'asesor').length}</h3>
                  <p className="mb-0">Afiliados</p>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card text-center bg-primary text-white">
                <div className="card-body">
                  <h3>{usuarios.filter(u => u.tipo === 'asociado').length}</h3>
                  <p className="mb-0">Asociados</p>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card text-center bg-success text-white">
                <div className="card-body">
                  <h3>{usuarios.filter(u => u.firebase_uid).length}</h3>
                  <p className="mb-0">Con Cuenta</p>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card text-center bg-warning text-white">
                <div className="card-body">
                  <h3>{usuarios.filter(u => !u.firebase_uid).length}</h3>
                  <p className="mb-0">Sin Cuenta</p>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card text-center bg-danger text-white">
                <div className="card-body">
                  <h3>{usuarios.length > 0 ? Math.round((usuarios.filter(u => !u.firebase_uid).length / usuarios.length) * 100) : 0}%</h3>
                  <p className="mb-0">Sin Acceso</p>
                </div>
              </div>
            </div>
          </div>

          {/* Controles de Filtrado */}
          <div className="card mb-4">
            <div className="card-header">
              <h6><i className="fas fa-filter me-2"></i>Filtros de Búsqueda</h6>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <label className="form-label">Buscar:</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Nombre, email o agencia..."
                    value={filtro}
                    onChange={(e) => setFiltro(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Estado de Cuenta:</label>
                  <select
                    className="form-select"
                    value={filtroEstado}
                    onChange={(e) => setFiltroEstado(e.target.value as any)}
                  >
                    <option value="todos">Todos los usuarios</option>
                    <option value="con_cuenta">✅ Con cuenta Firebase</option>
                    <option value="sin_cuenta">⚠️ Sin cuenta Firebase</option>
                    <option value="sin_email">❌ Sin email válido</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Tipo:</label>
                  <select
                    className="form-select"
                    value={filtroTipo}
                    onChange={(e) => setFiltroTipo(e.target.value as any)}
                  >
                    <option value="todos">Todos los tipos</option>
                    <option value="asesor">👨‍💼 Solo Afiliados</option>
                    <option value="asociado">👔 Solo Asociados</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Acciones:</label>
                  <div className="d-grid gap-2">
                    {filtroEstado === 'sin_cuenta' ? (
                      <button
                        className="btn btn-success"
                        onClick={handleBulkCreate}
                        disabled={creating || usuariosFiltrados.filter(u => !u.firebase_uid && u.correo !== 'Sin email' && u.correo.includes('@')).length === 0}
                      >
                        {creating ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Creando...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-user-plus me-2"></i>
                            Crear ({usuariosFiltrados.filter(u => !u.firebase_uid && u.correo !== 'Sin email' && u.correo.includes('@')).length})
                          </>
                        )}
                      </button>
                    ) : (
                      <button
                        className="btn btn-danger"
                        onClick={handleBulkReset}
                        disabled={resetting || usuariosFiltrados.filter(u => u.firebase_uid).length === 0}
                      >
                        {resetting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Enviando...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-mail-bulk me-2"></i>
                            Reset ({usuariosFiltrados.filter(u => u.firebase_uid).length})
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Estadísticas de filtrado */}
              <div className="mt-3">
                <div className="alert alert-light">
                  <div className="row text-center">
                    <div className="col-md-2">
                      <strong>{usuariosFiltrados.length}</strong><br />
                      <small>Total Filtrados</small>
                    </div>
                    <div className="col-md-2">
                      <strong>{usuariosFiltrados.filter(u => u.firebase_uid).length}</strong><br />
                      <small className="text-success">Con Cuenta</small>
                    </div>
                    <div className="col-md-2">
                      <strong>{usuariosFiltrados.filter(u => !u.firebase_uid && u.correo !== 'Sin email').length}</strong><br />
                      <small className="text-warning">Sin Cuenta</small>
                    </div>
                    <div className="col-md-2">
                      <strong>{usuariosFiltrados.filter(u => u.correo === 'Sin email' || !u.correo.includes('@')).length}</strong><br />
                      <small className="text-danger">Sin Email</small>
                    </div>
                    <div className="col-md-2">
                      <strong>{usuariosFiltrados.filter(u => u.tipo === 'asesor').length}</strong><br />
                      <small className="text-primary">Afiliados</small>
                    </div>
                    <div className="col-md-2">
                      <strong>{usuariosFiltrados.filter(u => u.tipo === 'asociado').length}</strong><br />
                      <small className="text-info">Asociados</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabla de usuarios */}
          <div className="table-responsive">
            <table className="table table-striped">
              <thead className="table-dark">
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Tipo</th>
                  <th>Agencia</th>
                  <th>Estado Firebase</th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                {usuariosFiltrados.map(usuario => (
                  <tr key={usuario.id}>
                    <td>{usuario.nombre}</td>
                    <td>
                      <code className={usuario.correo === 'Sin email' ? 'text-danger' : ''}>
                        {usuario.correo}
                      </code>
                    </td>
                    <td>
                      <span className={`badge ${usuario.tipo === 'asesor' ? 'bg-success' : 'bg-primary'}`}>
                        {usuario.tipo === 'asesor' ? 'Afiliado' : 'Asociado'}
                      </span>
                    </td>
                    <td>
                      <small className="text-muted">{usuario.agencia}</small>
                    </td>
                    <td>
                      {usuario.firebase_uid && usuario.correo !== 'Sin email' ? (
                        <span className="badge bg-success">
                          <i className="fas fa-check me-1"></i>
                          Cuenta activa
                        </span>
                      ) : !usuario.firebase_uid && usuario.correo !== 'Sin email' && usuario.correo.includes('@') ? (
                        <span className="badge bg-warning">
                          <i className="fas fa-exclamation me-1"></i>
                          Falta cuenta
                        </span>
                      ) : (
                        <span className="badge bg-danger">
                          <i className="fas fa-times me-1"></i>
                          Email inválido
                        </span>
                      )}
                    </td>
                    <td>
                      {usuario.firebase_uid && usuario.correo !== 'Sin email' ? (
                        <button
                          className="btn btn-sm btn-outline-warning"
                          onClick={() => handleSingleReset(usuario)}
                          disabled={resetting}
                        >
                          <i className="fas fa-key me-1"></i>
                          Reset
                        </button>
                      ) : usuario.correo !== 'Sin email' && usuario.correo.includes('@') ? (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleCreateAccount(usuario)}
                          disabled={creating}
                        >
                          <i className="fas fa-plus me-1"></i>
                          Crear
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => alert(`Usuario problemático: ${usuario.correo}. Considerar eliminar.`)}
                        >
                          <i className="fas fa-trash me-1"></i>
                          Revisar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Credenciales creadas */}
          {createdAccounts.length > 0 && (
            <div className="mt-4">
              <div className="card border-success">
                <div className="card-header bg-success text-white">
                  <h6 className="mb-0">
                    <i className="fas fa-key me-2"></i>
                    Cuentas Creadas ({createdAccounts.length})
                  </h6>
                </div>
                <div className="card-body">
                  <div className="d-flex justify-content-between mb-3">
                    <p className="mb-0">
                      Se han creado {createdAccounts.length} cuentas nuevas.
                      <strong className="text-danger"> Guarde estas credenciales antes de cerrar esta página.</strong>
                    </p>
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        const data = JSON.stringify(createdAccounts, null, 2);
                        const blob = new Blob([data], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `credenciales_${new Date().toISOString().split('T')[0]}.json`;
                        a.click();
                      }}
                    >
                      <i className="fas fa-download me-2"></i>
                      Descargar JSON
                    </button>
                  </div>
                  <div className="table-responsive" style={{ maxHeight: '300px' }}>
                    <table className="table table-sm table-striped">
                      <thead className="table-dark sticky-top">
                        <tr>
                          <th>Email</th>
                          <th>Contraseña Temporal</th>
                          <th>UID Firebase</th>
                        </tr>
                      </thead>
                      <tbody>
                        {createdAccounts.map((account, index) => (
                          <tr key={index}>
                            <td><code>{account.email}</code></td>
                            <td><code>{account.password}</code></td>
                            <td><small className="text-muted">{account.uid}</small></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Resultados del reset masivo */}
          {resetResults.length > 0 && (
            <div className="mt-4">
              <h6>Resultados del Reset Masivo:</h6>
              <div className="row">
                <div className="col-md-6">
                  <h6 className="text-success">✅ Exitosos ({resetResults.filter(r => r.success).length})</h6>
                  <ul className="list-group list-group-flush" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {resetResults.filter(r => r.success).map((result, index) => (
                      <li key={index} className="list-group-item py-1">
                        <small className="text-success">{result.email}</small>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="col-md-6">
                  <h6 className="text-danger">❌ Fallidos ({resetResults.filter(r => !r.success).length})</h6>
                  <ul className="list-group list-group-flush" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {resetResults.filter(r => !r.success).map((result, index) => (
                      <li key={index} className="list-group-item py-1">
                        <small className="text-danger">
                          {result.email}: {result.error}
                        </small>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Instrucciones */}
          <div className="alert alert-info mt-4">
            <h6>
              <i className="fas fa-info-circle me-2"></i>
              Instrucciones para usuarios
            </h6>
            <ol className="mb-0">
              <li>Los usuarios recibirán un email de "Restablecer contraseña" de Firebase</li>
              <li>Deben hacer clic en el enlace del email</li>
              <li>Podrán crear una nueva contraseña</li>
              <li>Después podrán iniciar sesión normalmente con email + nueva contraseña</li>
            </ol>
          </div>

          {/* Información importante */}
          <div className="alert alert-warning">
            <h6>
              <i className="fas fa-exclamation-triangle me-2"></i>
              Información importante
            </h6>
            <ul className="mb-0">
              <li><strong>Contraseñas originales perdidas</strong>: Las contraseñas generadas durante la migración no se pueden recuperar</li>
              <li><strong>Reset es la única opción</strong>: Firebase no permite ver contraseñas hasheadas</li>
              <li><strong>Proceso seguro</strong>: Los emails de reset expiran en 1 hora</li>
              <li><strong>Un solo uso</strong>: Cada enlace de reset solo funciona una vez</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}